// components/JobApplicationsCard.tsx
import React from 'react';
import { Application } from '../model/JobApplicationsModel';
import { formatDate } from '../../../../utils/date';
import { getJobTitleInitials } from '../../../../utils/jobTitleDefault';

interface JobApplicationCardProps {
  applications: Application[];
  onViewDetails: (id: string) => void;
  viewMode?: 'grid' | 'list';
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'applied': return 'bg-blue-50 text-blue-700';
    case 'screening': return 'bg-yellow-50 text-yellow-700';
    case 'interviewing': return 'bg-purple-50 text-purple-700';
    case 'offered':
    case 'hired': return 'bg-green-50 text-green-700';
    case 'rejected': return 'bg-red-50 text-red-700';
    case 'withdrawn': return 'bg-gray-50 text-gray-700';
    default: return 'bg-gray-50 text-gray-700';
  }
};

const formatEnum = (value?: string) =>
  value
    ? value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
    : '';

const JobApplicationCard: React.FC<JobApplicationCardProps> = ({
  applications,
  onViewDetails,
  viewMode = 'grid',
}) => {
  if (applications.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12 bg-white rounded-xl border border-gray-200 px-4">
        <div className="text-gray-400 mb-4">
          <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
          </svg>
        </div>
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No applications yet</h3>
        <p className="text-sm sm:text-base text-gray-600">Apply for jobs to see them here</p>
      </div>
    );
  }

  // ====== LIST VIEW (matching JobCard list view alignment) ======
  if (viewMode === 'list') {
    return (
      <div className="space-y-3 sm:space-y-4">
        {applications.map(app => {
          const jobInitials = getJobTitleInitials(app.job.company_name);

          return (
            <div
              key={app.id}
              className="bg-white rounded-md border shadow-sm hover:shadow-md transition-shadow duration-200 p-3"
            >
              {/* Top Section */}
              <div className="flex items-start justify-between gap-6 pb-3">
                {/* Avatar */}
                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <span className="text-indigo-600 font-medium text-sm">
                    {jobInitials}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      {/* Job Title */}
                      <h2 className="text-sm font-medium text-gray-900 truncate">
                        {app.job.title}
                      </h2>

                      {/* Company */}
                      <p className="text-xs text-gray-500 truncate">
                        {app.job.company_name || "Company"}
                      </p>
                    </div>

                    {/* Salary + Applied Date (Top Right like JobCard list view) */}
                    <div className="flex flex-col items-end ml-4">
                      <div className="mb-1">
                        <span className="text-xs font-medium text-gray-700">
                          {app.job.salary_currency} {app.job.salary_range || "Not Disclosed"}
                        </span>
                      </div>

                      <div className="text-gray-500 text-xs">
                        Applied:{" "}
                        <span className="font-medium">
                          {formatDate(app.applied_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Badges Row */}
              <div className="flex flex-wrap gap-2 mb-2">
                {app.job.job_type && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {formatEnum(app.job.job_type)}
                  </span>
                )}

                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                    app.status
                  )}`}
                >
                  {formatEnum(app.status)}
                </span>
              </div>

              {/* Location + View Details (Same line like JobCard LIST) */}
              {app.job.location && (
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    <span>{app.job.location}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onViewDetails(app.id)}
                      className="flex-1 text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                    >
                      <span>View</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ====== GRID VIEW (matching JobCard grid view alignment) ======
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      {applications.map(app => {
        const jobInitials = getJobTitleInitials(app.job.company_name);

        return (
          <div key={app.id} className="bg-white rounded-md border shadow-sm hover:shadow-md transition-shadow duration-200 p-3">
            {/* Header with logo and title */}
            <div className="flex items-start justify-between gap-6 pb-3">
              {/* Logo */}
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-medium text-sm">{jobInitials}</span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-2">
                  {/* Job Title with proper truncation */}
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-medium text-gray-900 truncate" title={app.job.title}>
                      {app.job.title}
                    </h2>
                    <p className="text-xs text-gray-500 truncate" title={app.job.company_name}>
                      {app.job.company_name || 'Company'}
                    </p>
                  </div>

                  {/* Status Badge - Top Right Corner */}
                  <span className={`px-2 py-0.5 text-[11px] rounded-full font-medium ${getStatusColor(app.status)}`}>
                    {formatEnum(app.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Job Type Badges */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-1 sm:mb-2">
              {app.job.job_type && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  {formatEnum(app.job.job_type)}
                </span>
              )}
            </div>

            {/* Location */}
            {app.job.location && (
              <div className="text-xs text-gray-500 ">
                <span className="truncate" title={app.job.location}>
                  {app.job.location}
                </span>
              </div>
            )}

            <div className="mb-1">
              <span className="text-xs font-medium text-gray-700">
                {app.job.salary_currency && app.job.salary_range
                  ? `${app.job.salary_currency} ${app.job.salary_range}`
                  : app.job.salary_range || 'Not Disclosed'}
              </span>
            </div>

            {/* Footer */}
            <div>
              <div className="flex items-center justify-between">
                <div className="flex-1 text-gray-500 text-[10px] sm:text-xs">
                  Applied:{" "}
                  <span className="font-medium">
                    {formatDate(app.applied_at)}
                  </span>
                </div>

                <button
                  onClick={() => onViewDetails(app.id)}
                  className="flex-1 text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                >
                  <span>View</span>
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default JobApplicationCard;